import React from 'react';
import {Box, Card, CardContent} from "@mui/material";
import {Typography} from "@mui/joy";
import SectionStatusChip from "./SectionStatusChip";
import {SectionStatus} from "../../utils/contaminationDeclaration/gating";

interface Props {
    number: number;
    title: string;
    status: SectionStatus;
    /** Live count of required, active, unanswered questions in this section. */
    openCount: number;
    /** Short explanatory line under the title (e.g. "pre-filled — verify"). */
    hint?: string;
    /** Extra control rendered right of the status chip (e.g. "Set remaining to No"). */
    headerAction?: React.ReactNode;
    testId?: string;
    children: React.ReactNode;
}

/**
 * One numbered section: the header (number, title, status) sits on the grey
 * page surface, the questions in a white card below — the prototype's layout.
 * The number badge is the compact WizardStepper step icon (filled primary,
 * white bold number); the card is borderless (white on the grey surface is
 * contrast enough) to keep the form calm.
 */
const SectionCard = ({number, title, status, openCount, hint, headerAction, testId, children}: Props) => (
    <Box id={testId} data-testid={testId} sx={{scrollMarginTop: "12px"}}>
        <Box sx={{display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", mb: hint ? "4px" : "12px"}}>
            <Box
                sx={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    backgroundColor: "primary.main", color: "#fff", fontSize: "12px", fontWeight: "bold",
                }}
            >
                {number}
            </Box>
            <Typography level="titleMedium" sx={{fontSize: "18px", lineHeight: "25px", color: "#333", flex: 1, minWidth: 0}}>
                {title}
            </Typography>
            <SectionStatusChip status={status} openCount={openCount}/>
            {headerAction}
        </Box>
        {hint && (
            <Typography level="body-xs" sx={{color: 'text.tertiary', mb: "12px", ml: "40px"}}>
                {hint}
            </Typography>
        )}
        <Card sx={{border: "none", borderRadius: "4px", boxShadow: "none", backgroundColor: "#fff"}}>
            <CardContent sx={{padding: "24px", "&:last-child": {paddingBottom: "24px"}}}>
                {children}
            </CardContent>
        </Card>
    </Box>
);

export default SectionCard;
